package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.data.dto.EventPlayerStatsResultDto
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.data.dto.SportStatTypeDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.events.formatOptionsForSport
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

@Composable
fun MatchView(
    state: EventDetailUiState,
    onEndMatch: () -> Unit = {},
    onScoreUp: (teamId: String) -> Unit = {},
    onScoreDown: (teamId: String) -> Unit = {},
    onToggleTimer: () -> Unit = {},
    onResetTimer: () -> Unit = {},
    onTimerTick: () -> Unit = {},
    onToggleExtendedStats: () -> Unit = {},
    onViewProfile: (userId: String) -> Unit = {},
    onSetPlayerStat: (userId: String, statTypeId: String, value: Int) -> Unit = { _, _, _ -> },
    onSwapLineup: (userIdA: String, userIdB: String) -> Unit = { _, _ -> },
    onUpdateFormat: (Int) -> Unit = {},
    onSwapEventTeams: (teamIdA: String, teamIdB: String) -> Unit = { _, _ -> },
    modifier: Modifier = Modifier
) {
    val event = state.event ?: return
    val playerStats = state.playerStats

    // Tick the timer every second when running
    LaunchedEffect(state.timerRunning) {
        if (state.timerRunning) {
            val intervalId = window.setInterval({ onTimerTick() }, 1000)
            kotlinx.coroutines.suspendCancellableCoroutine<Unit> { cont ->
                cont.invokeOnCancellation { window.clearInterval(intervalId) }
            }
        }
    }

    // Format toggle is still useful for previewing different team sizes
    // locally. The per-format coordinate Log/Reset workflow that lived here
    // was removed once the hand-tuned defaults landed for every format —
    // admins no longer reposition by dragging.
    // `formatOverride` is now an optimistic local layer over the
    // server-side `event.format`. When the admin picks a new size we set
    // it immediately for instant UI feedback and fire `onUpdateFormat`
    // which PUTs the new `format` on the event. The backend broadcasts
    // `EVENT_UPDATED`, the WS handler refreshes `state.event`, and the
    // `LaunchedEffect` below clears the override once the server-side
    // value catches up so we don't double-source.
    var formatOverride by remember { mutableStateOf<Int?>(null) }
    val effectiveFormat = formatOverride ?: event.format ?: 0
    LaunchedEffect(event.format) {
        if (formatOverride == event.format) formatOverride = null
    }

    // Visible team slots. Backend returns teams sorted by
    // `display_order ASC NULLS LAST, created_at`, so the first two are the
    // canonical pair every viewer sees. Cycling the chevron triggers a
    // `swapEventTeams` on the backend (swaps two teams' `display_order`),
    // and the resulting `EVENT_UPDATED` broadcast re-renders the pair for
    // everyone.
    val teamsCount = event.teams.size
    val visibleTeams: List<EventTeamDto> = event.teams.take(2)

    val onCycleTeamSlot: (Int) -> Unit = { slotIdx ->
        if (teamsCount > 2) {
            val currentInSlot = visibleTeams.getOrNull(slotIdx)
            val otherSlotTeam = visibleTeams.getOrNull(1 - slotIdx)
            if (currentInSlot != null && otherSlotTeam != null) {
                // Pick the next team in the list that isn't currently in
                // the OTHER slot (so we never render duplicates) and
                // isn't this slot's team (no-op). Walk past the current
                // index forward, wrapping at the end.
                val currentIdx = event.teams.indexOf(currentInSlot)
                var nextIdx = (currentIdx + 1) % teamsCount
                while (
                    event.teams[nextIdx].id == currentInSlot.id ||
                    event.teams[nextIdx].id == otherSlotTeam.id
                ) {
                    nextIdx = (nextIdx + 1) % teamsCount
                    if (nextIdx == currentIdx) break
                }
                if (event.teams[nextIdx].id != currentInSlot.id) {
                    onSwapEventTeams(currentInSlot.id, event.teams[nextIdx].id)
                }
            }
        }
    }

    // Swap state — when an admin taps a card it becomes "selected" (scales
    // up + glows). Tapping a second card swaps the two players (the slot
    // override map records the new player ID at each slot). Tapping the
    // selected card again deselects. Cleared on format change so a stale
    // selection doesn't survive a layout swap.
    var selectedSlotKey by remember { mutableStateOf<String?>(null) }
    var selectedPlayerId by remember { mutableStateOf<String?>(null) }
    var slotPlayerOverrides by remember { mutableStateOf<Map<String, String?>>(emptyMap()) }
    // Whenever the backend pushes a new participant list (post-swap or any
    // other reason), drop the optimistic overrides — the canonical order
    // now lives in `event.participants` and re-applying the local map
    // would double-swap the affected players.
    LaunchedEffect(event.participants.map { it.id to it.eventTeamId }) {
        slotPlayerOverrides = emptyMap()
        selectedSlotKey = null
        selectedPlayerId = null
    }
    LaunchedEffect(effectiveFormat) {
        selectedSlotKey = null
        selectedPlayerId = null
        slotPlayerOverrides = emptyMap()
    }

    val participantById = remember(event.participants) {
        event.participants.associateBy { it.id }
    }
    // Slot keys carry the team index in their second segment, which lets
    // us reject cross-team swaps without consulting the participant DTOs:
    //   • Field key: `"$teamIdx-$rowIdx-$col"`        → split index 0
    //   • Bench key: `"bench-$teamIdx-$benchIdx"`     → split index 1
    fun teamIndexFromSlotKey(slotKey: String): Int? {
        val parts = slotKey.split("-")
        return if (slotKey.startsWith("bench-")) parts.getOrNull(1)?.toIntOrNull()
        else parts.getOrNull(0)?.toIntOrNull()
    }

    val onTapSlot: (slotKey: String, currentPlayer: EventParticipantDto?) -> Unit =
        { slotKey, currentPlayer ->
            when {
                selectedSlotKey == null -> {
                    selectedSlotKey = slotKey
                    selectedPlayerId = currentPlayer?.id
                }
                selectedSlotKey == slotKey -> {
                    selectedSlotKey = null
                    selectedPlayerId = null
                }
                else -> {
                    val otherKey = selectedSlotKey!!
                    // Block swaps between teams — a left-team starter can't
                    // jump into a right-team slot. Re-select the new slot
                    // instead so the admin can keep tapping until they pick
                    // a same-team target.
                    val selectedTeam = teamIndexFromSlotKey(otherKey)
                    val newTeam = teamIndexFromSlotKey(slotKey)
                    if (selectedTeam != null && newTeam != null && selectedTeam != newTeam) {
                        selectedSlotKey = slotKey
                        selectedPlayerId = currentPlayer?.id
                    } else {
                        val otherPlayerId = selectedPlayerId
                        // Persist on the backend whenever both slots hold a
                        // real player — empty placeholders (e.g. an unfilled
                        // bench slot) have no `userId` to swap, so we keep
                        // those as local-only overrides. Backend's swap API
                        // is keyed by `user_id`, so we look the participant
                        // up via `participantById` to translate the
                        // participant.id we have here into its `userId`.
                        val userA = otherPlayerId?.let { participantById[it]?.userId }
                        val userB = currentPlayer?.userId
                        if (userA != null && userB != null) {
                            onSwapLineup(userA, userB)
                        }
                        slotPlayerOverrides = slotPlayerOverrides +
                            (slotKey to otherPlayerId) +
                            (otherKey to currentPlayer?.id)
                        selectedSlotKey = null
                        selectedPlayerId = null
                    }
                }
            }
        }

    Box(
        modifier = modifier
            .fillMaxSize()
            .position(Position.Relative)
            // Switched from `overflow: hidden` to `visible` so cards near the
            // top of the field aren't clipped by the MatchView's top edge —
            // horizontal overflow is still clipped by the page-level wrapper.
            .styleModifier { property("overflow", "visible") }
    ) {
        // Content on top of field
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sGap(0)
        ) {
            TopBar(
                event = event,
                state = state,
                onScoreUp = onScoreUp,
                onScoreDown = onScoreDown,
                onToggleTimer = onToggleTimer,
                onResetTimer = onResetTimer,
                onEndMatch = onEndMatch,
                onToggleExtendedStats = onToggleExtendedStats,
                overrideFormat = formatOverride,
                onFormatChange = { picked ->
                    if (picked != null && picked != event.format) {
                        // Optimistic local + persist on backend. The WS
                        // round-trip will replace `event.format` and the
                        // `LaunchedEffect` clears the override.
                        formatOverride = picked
                        onUpdateFormat(picked)
                    } else {
                        // Picking the event's real format (or "no change")
                        // is a no-op on the server — just clear the local
                        // override so we resync.
                        formatOverride = null
                    }
                },
            )

            FieldFormationArea(
                event = event,
                visibleTeams = visibleTeams,
                onCycleTeamSlot = onCycleTeamSlot,
                isAdmin = state.isAdmin,
                effectiveFormat = effectiveFormat,
                slotPlayerOverrides = slotPlayerOverrides,
                selectedSlotKey = selectedSlotKey,
                participantById = participantById,
                playerStats = playerStats,
                onSetPlayerStat = onSetPlayerStat,
                onTapSlot = onTapSlot,
                onViewProfile = onViewProfile,
            )

            BenchRow(
                event = event,
                visibleTeams = visibleTeams,
                isAdmin = state.isAdmin,
                effectiveFormat = effectiveFormat,
                slotPlayerOverrides = slotPlayerOverrides,
                selectedSlotKey = selectedSlotKey,
                participantById = participantById,
                onTapSlot = onTapSlot,
                onViewProfile = onViewProfile,
            )
        }
    }
}

/**
 * Read-only twin of [MatchView] used by the Pro Lineups tab on completed
 * events. Renders the exact same field formation + bench as the live match
 * view (same card geometry, same per-format position defaults, same team
 * crests) but without the top scoreboard or any admin interactions —
 * stat-set / swap / cycle callbacks are no-ops so the view is purely
 * informational.
 */
@Composable
fun LineupsFieldDisplay(
    state: EventDetailUiState,
    onViewProfile: (userId: String) -> Unit = {},
    formatOverride: Int? = null,
    onRatePlayer: (userId: String, attributeRatings: Map<String, Int>) -> Unit = { _, _ -> },
    modifier: Modifier = Modifier,
) {
    val event = state.event ?: return
    val effectiveFormat = formatOverride ?: event.format ?: 0
    val teamsCount = event.teams.size

    // Local pair of visible team indices — cycling rotates which two of the
    // event's 3+ teams sit on the field without touching the backend
    // display order (unlike the live view, which mutates it).
    var visibleSlots by remember(event.id) { mutableStateOf(listOf(0, 1)) }
    val visibleTeams: List<EventTeamDto> = visibleSlots
        .mapNotNull { idx -> event.teams.getOrNull(idx) }
    val participantById = remember(event.participants) {
        event.participants.associateBy { it.id }
    }

    val onCycleTeamSlot: (Int) -> Unit = { slotIdx ->
        if (teamsCount > 2) {
            val currentIdx = visibleSlots.getOrElse(slotIdx) { 0 }
            val otherSlotIdx = visibleSlots.getOrElse(1 - slotIdx) { 0 }
            var nextIdx = (currentIdx + 1) % teamsCount
            // Skip indices already shown in the other slot so we never put
            // the same team in both slots.
            while (nextIdx == currentIdx || nextIdx == otherSlotIdx) {
                nextIdx = (nextIdx + 1) % teamsCount
                if (nextIdx == currentIdx) break
            }
            if (nextIdx != currentIdx) {
                visibleSlots = visibleSlots.toMutableList().also { it[slotIdx] = nextIdx }
            }
        }
    }

    // Tapped card opens the Rate Performance popup (Figma 8691:235407).
    var ratedPlayer by remember { mutableStateOf<EventParticipantDto?>(null) }

    // Aggregate every reviewer's generalRating per ratedUser, so each
    // card's ★ overlay can show the event-scoped community average
    // instead of the player's all-events OVR. Re-computes only when
    // the ratings list changes.
    val eventRatingAverages: Map<String, Double> = remember(state.ratings) {
        state.ratings
            .groupBy { it.ratedUserId }
            .mapValues { (_, list) ->
                list.mapNotNull { it.generalRating.toDoubleOrNull() }
                    .takeIf { it.isNotEmpty() }
                    ?.average()
                    ?: 0.0
            }
            .filterValues { it > 0.0 }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .position(Position.Relative)
            .styleModifier { property("overflow", "visible") }
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            // Render the live TopBar invisibly so the formation field starts
            // at the exact same Y as in IN_PROGRESS — height adapts to team
            // count and admin status automatically instead of a hardcoded
            // px guess.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("visibility", "hidden")
                        property("pointer-events", "none")
                    }
            ) {
                TopBar(
                    event = event,
                    state = state,
                    onScoreUp = {},
                    onScoreDown = {},
                    onToggleTimer = {},
                    onResetTimer = {},
                    onEndMatch = {},
                    onToggleExtendedStats = {},
                    overrideFormat = null,
                    onFormatChange = {},
                )
            }

            FieldFormationArea(
                event = event,
                visibleTeams = visibleTeams,
                onCycleTeamSlot = onCycleTeamSlot,
                isAdmin = false,
                effectiveFormat = effectiveFormat,
                slotPlayerOverrides = emptyMap(),
                selectedSlotKey = null,
                participantById = participantById,
                playerStats = state.playerStats,
                onSetPlayerStat = { _, _, _ -> },
                onTapSlot = { _, _ -> },
                onViewProfile = onViewProfile,
                forceShowTeamArrows = true,
                showRating = true,
                hideZeroStats = true,
                onPlayerClick = { p -> ratedPlayer = p },
                eventRatingAverages = eventRatingAverages,
            )

            BenchRow(
                event = event,
                visibleTeams = visibleTeams,
                isAdmin = false,
                effectiveFormat = effectiveFormat,
                slotPlayerOverrides = emptyMap(),
                selectedSlotKey = null,
                participantById = participantById,
                onTapSlot = { _, _ -> },
                onViewProfile = onViewProfile,
                playerStats = state.playerStats,
                onPlayerClick = { p -> ratedPlayer = p },
                showRating = true,
                hideZeroStats = true,
                eventRatingAverages = eventRatingAverages,
            )
        }

        ratedPlayer?.let { player ->
            // Existing review by the current viewer for this player in
            // this event (if any) so the popup pre-fills with their
            // previous star picks → seamless re-vote.
            val viewerId = com.arena_web.utils.Storage.get(
                com.arena_web.utils.StorageKeys.USER_ID
            )
            val previousReview = state.ratings.firstOrNull {
                it.ratedUserId == player.userId && it.reviewerId == viewerId
            }
            val previousByAttribute = previousReview
                ?.details
                ?.associate { it.attributeId to (it.value.toDoubleOrNull()?.toInt() ?: 0) }
                ?: emptyMap()
            // Average of all reviewers' generalRating for this player in
            // this event — that's what gets fed into the popup's
            // community summary block.
            val playerRatings = state.ratings.filter { it.ratedUserId == player.userId }
            val communityAvg = playerRatings
                .mapNotNull { it.generalRating.toDoubleOrNull() }
                .takeIf { it.isNotEmpty() }
                ?.average()
            val communityCount = playerRatings.size

            // Close after a submit only once the request finishes. We
            // remember which submit we triggered, then drop `ratedPlayer`
            // when `ratingSubmitInProgress` flips false (i.e., the POST
            // resolved — success or failure).
            var submitTriggered by remember(player.userId) { mutableStateOf(false) }
            LaunchedEffect(state.ratingSubmitInProgress, submitTriggered) {
                if (submitTriggered && !state.ratingSubmitInProgress) {
                    ratedPlayer = null
                    submitTriggered = false
                }
            }
            RatePlayerPopup(
                player = player,
                sport = event.sportName,
                initialRatings = previousByAttribute,
                communityAverage = communityAvg,
                communityCount = communityCount,
                isSubmitting = state.ratingSubmitInProgress,
                // Backend rejects self-rating — surface the same gate
                // on the UI by hiding the Submit button when the viewer
                // is looking at their own card.
                isSelf = viewerId != null && viewerId == player.userId,
                onViewProfile = { uid ->
                    ratedPlayer = null
                    onViewProfile(uid)
                },
                onClose = {
                    if (!state.ratingSubmitInProgress) ratedPlayer = null
                },
                onSubmit = { ratings ->
                    onRatePlayer(player.userId, ratings)
                    submitTriggered = true
                },
            )
        }
    }
}

// ── Top Bar ──

@Composable
private fun TopBar(
    event: EventDetailDto,
    state: EventDetailUiState,
    onScoreUp: (String) -> Unit,
    onScoreDown: (String) -> Unit,
    onToggleTimer: () -> Unit,
    onResetTimer: () -> Unit,
    onEndMatch: () -> Unit,
    onToggleExtendedStats: () -> Unit,
    overrideFormat: Int?,
    onFormatChange: (Int?) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Relative)
    ) {
        // Left: Scoreboard + Pro Stat
        Row(
            modifier = Modifier.sGap(68),
            verticalAlignment = Alignment.Top
        ) {
            MatchScoreboard(
                event = event,
                timerSeconds = state.timerSeconds,
                timerRunning = state.timerRunning,
                isAdmin = state.isAdmin,
                onScoreUp = onScoreUp,
                onScoreDown = onScoreDown,
                onToggleTimer = onToggleTimer,
                onResetTimer = onResetTimer
            )

            // ProStat toggle hidden until the extended-stats feature ships;
            // the composable (`ProStatToggle`) is left intact further down
            // so it can be wired back in with a single render.
        }

        // Timeline button hidden — the feature isn't ready yet, the button
        // was rendering as a no-op TODO. `TimelineButton` is still defined
        // below so re-enabling it later is a one-line composable call.

        // Right cluster: format dropdown + End Match button (admin-only).
        // The pill style mirrors `SportSelectorPill` in AppHeader; an
        // explicit `z-index: 2000` on this Row keeps the format dropdown
        // painted on top of the field cards (which can go up to
        // z-index 1000 when dragged).
        if (state.isAdmin) {
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sGap(12)
                    .styleModifier {
                        property("right", "0")
                        property("top", "0")
                        property("z-index", "2000")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                FormatOverridePill(
                    overrideFormat = overrideFormat,
                    onFormatChange = onFormatChange,
                    sportName = event.sportName,
                    fallbackFormat = event.format,
                )
                EndMatchButton(onClick = onEndMatch)
            }
        }
    }
}

/**
 * Pill-shaped local format-override dropdown for the live match view.
 * Visually mirrors `SportSelectorPill` in `AppHeader` — same gradient,
 * border, `Label: value ▾` layout — so the override reads as a
 * first-class control instead of a debug toolbar.
 *
 * Options come from `formatOptionsForSport(sportName)` (same set as the
 * create-event popup). The pill always shows the effective format (the
 * override if set, else the event's real format). Selecting the option
 * that matches the event's real format clears the override.
 */
@Composable
internal fun FormatOverridePill(
    overrideFormat: Int?,
    onFormatChange: (Int?) -> Unit,
    sportName: String,
    fallbackFormat: Int?,
    // Mobile drops the "Format:" caption — the value + chevron alone read
    // fine and the header row is tight on space.
    showLabel: Boolean = true,
    // Anchor the dropdown to the pill's RIGHT edge so it grows leftwards.
    // The mobile match view pins the pill to the right side of the screen —
    // a left-anchored dropdown (wider than the pill) would run off-screen.
    alignDropdownEnd: Boolean = false,
    // Dropdown min-width in s-units. The mobile match view passes a smaller
    // one — the desktop 180 reads oversized next to the compact pill there.
    dropdownMinWidth: Number = 180,
) {
    val t = LocalAppStrings.current
    var expanded by remember { mutableStateOf(false) }
    val effective = overrideFormat ?: fallbackFormat
    val options = formatOptionsForSport(sportName)
    val selectedLabel = options.find { it.value == effective?.toString() }?.label
        ?: "${effective ?: "?"}×${effective ?: "?"}"

    Box(modifier = Modifier.position(Position.Relative)) {
        Row(
            modifier = Modifier
                .sHeight(48)
                .sBorderRadius(26)
                .sPadding(8, 18)
                .sGap(12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
                .onClick { expanded = !expanded },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (showLabel) {
                SpanText(
                    t.eventFormatLabel,
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
            }
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    selectedLabel,
                    modifier = Modifier.bodyLMedium().color(LocalSportColors.current.metricsPrimary)
                )
                Icon(src = AppIcons.ChevronDown)
            }
        }

        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(56)
                    .then(if (alignDropdownEnd) Modifier.sRight(0) else Modifier.sLeft(0))
                    .sBorderRadius(16)
                    .sPadding(8)
                    .zIndex(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("min-width", "calc($dropdownMinWidth * var(--scale, 1))")
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
                    options.forEach { option ->
                        val isSelected = option.value == effective?.toString()
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sBorderRadius(10)
                                .sPadding(12, 16)
                                .cursor(Cursor.Pointer)
                                .then(
                                    if (isSelected) Modifier.styleModifier {
                                        property("background", "rgba(0, 97, 255, 0.12)")
                                    } else Modifier
                                )
                                .onClick {
                                    val parsed = option.value.toIntOrNull()
                                    // Selecting the event's real format
                                    // clears the override so we stop
                                    // diverging from the server-side state.
                                    onFormatChange(
                                        if (parsed == fallbackFormat) null else parsed
                                    )
                                    expanded = false
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                option.label,
                                modifier = Modifier.bodyLMedium().color(
                                    if (isSelected) LocalSportColors.current.metricsPrimary
                                    else AppColors.Text.Secondary
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}

// ── Match Scoreboard ──

@Composable
private fun MatchScoreboard(
    event: EventDetailDto,
    timerSeconds: Long,
    timerRunning: Boolean,
    isAdmin: Boolean,
    onScoreUp: (String) -> Unit,
    onScoreDown: (String) -> Unit,
    onToggleTimer: () -> Unit,
    onResetTimer: () -> Unit
) {
    val t = LocalAppStrings.current
    // Sport-aware colours so the Reset Timer pill, score-control arrows etc.
    // pick up the same palette as the rest of the screen instead of being
    // locked to a single sport's hex.
    val sportColors = LocalSportColors.current
    Row(verticalAlignment = Alignment.Top) {
        // Left: Scoreboard stack — score panel + timer + Reset, in normal flow.
        // Height is content-driven so the Reset pill never gets shrunk by the
        // flex container; the canvas reference in Figma (node 7904:364516) is
        // 138×136 but the Reset there sits below the timer too, just clipped
        // by an outer absolute frame we don't have here.
        Column(modifier = Modifier.sWidth(138)) {
            // Score-panel + timer wrapper — gradient bg + rounded corners
            // (tl:8, bl:8, br:8). Reset lives outside this wrapper so it
            // gets its own solid-blue background.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("background", "linear-gradient(176deg, rgba(60,121,241,0.135) 13.9%, rgba(5,4,12,0.03) 69.5%)")
                        property("border-radius", "${s(8)} 0 ${s(8)} ${s(8)}")
                    }
            ) {
                // Match Panel — scores (light gray bg)
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .position(Position.Relative)
                        .backgroundColor(Color.rgb(235, 235, 235))
                        .sPadding(4, 6)
                        .styleModifier {
                            property("border-radius", "${s(6)} 0 0 0")
                        }
                ) {
                    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                        event.teams.forEach { team ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(
                                    modifier = Modifier.sGap(8),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    SpanText(
                                        team.name.take(4).uppercase(),
                                        modifier = Modifier
                                            .sFontSize(18)
                                            .fontWeight(FontWeight.Bold)
                                            .color(AppColors.PrimaryBlack)
                                            .styleModifier {
                                                property("letter-spacing", s(0.09))
                                                property("line-height", s(22))
                                            }
                                    )
                                    if (team.officialTeamImageUrl != null) {
                                        Image(
                                            src = team.officialTeamImageUrl,
                                            modifier = Modifier.sSize(22)
                                                .styleModifier { property("object-fit", "contain") }
                                        )
                                    }
                                }
                                SpanText(
                                    "${team.score}",
                                    modifier = Modifier
                                        .sFontSize(22)
                                        .fontWeight(FontWeight.Bold)
                                        .color(AppColors.PrimaryBlack)
                                        .styleModifier {
                                            property("letter-spacing", s(0.22))
                                            property("line-height", s(26))
                                        }
                                )
                            }
                        }
                    }

                    // Vertical dividers between team name and score — one at
                    // the top edge of the panel and one at the bottom. The
                    // previous version wrapped both bars in a 72px-tall Column
                    // with `SpaceBetween`; once the panel grew with dynamic
                    // team-row heights, the second bar landed outside the
                    // visible area and only the top stub rendered. Pin each
                    // bar to its own edge with `top` / `bottom` so both stay
                    // on screen regardless of how tall the team rows get.
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sWidth(2).sHeight(12)
                            .backgroundColor(AppColors.PrimaryBlack)
                            .styleModifier {
                                property("left", s(97))
                                property("top", "0")
                                property("border-radius", "0 0 1px 1px")
                            }
                    )
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sWidth(2).sHeight(12)
                            .backgroundColor(AppColors.PrimaryBlack)
                            .styleModifier {
                                property("left", s(97))
                                property("bottom", "0")
                                property("border-radius", "1px 1px 0 0")
                            }
                    )
                }

                // Timer row — black bg
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .backgroundColor(AppColors.PrimaryBlack)
                        .sPadding(4, 8)
                        .position(Position.Relative)
                        .styleModifier {
                            property("border-radius", "0 0 ${s(8)} ${s(8)}")
                        }
                ) {
                    // Timer display
                    val mins = (timerSeconds / 60).toString().padStart(2, '0')
                    val secs = (timerSeconds % 60).toString().padStart(2, '0')
                    SpanText(
                        "$mins : $secs",
                        modifier = Modifier
                            .sFontSize(20)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.PrimaryWhite)
                            .styleModifier {
                                property("letter-spacing", s(0.2))
                                property("line-height", s(26))
                            }
                    )

                    // Timer control button — Pause (running) | Play (stopped).
                    // Admin-only: regular viewers see the scoreboard read-only.
                    if (isAdmin) {
                        Box(
                            modifier = Modifier
                                .position(Position.Absolute)
                                .sPadding(10)
                                .styleModifier {
                                    property("left", s(98))
                                    property("top", s(-5))
                                }
                                .cursor(Cursor.Pointer)
                                .onClick { onToggleTimer() }
                        ) {
                            Box(
                                modifier = Modifier
                                    .sSize(24)
                                    .sBorderRadius(100)
                                    .backgroundColor(Color.rgb(30, 127, 245))
                                    .overflow(Overflow.Hidden),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    src = if (timerRunning) AppIcons.PauseSmall else AppIcons.Play,
                                    modifier = Modifier.sSize(if (timerRunning) 10 else 16).styleModifier {
                                        property("filter", "brightness(0) invert(1)")
                                    }
                                )
                            }
                        }
                    }
                }
            }

            // Reset Timer button — Figma 7904:364516 / "Timer Control / Reset".
            // Solid sport-primary pill 93×37. Admin-only since it nukes the
            // elapsed match time. (The original 0.3px white-40% ring on
            // left/right/bottom was dropped — the pill blends cleanly into
            // the timer bar above without it.)
            if (isAdmin) {
                Box(
                    modifier = Modifier
                        .sWidth(93)
                        .sHeight(37)
                        .cursor(Cursor.Pointer)
                        .onClick { onResetTimer() }
                        .overflow(Overflow.Hidden)
                        .backgroundColor(sportColors.primary)
                        .styleModifier {
                            property("border-radius", "0 0 ${s(10)} ${s(10)}")
                            property("box-sizing", "border-box")
                            property("flex-shrink", "0")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.eventReset,
                        modifier = Modifier
                            .sFontSize(16)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .fontWeight(FontWeight.Medium)
                            .color(sportColors.onPrimary)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }

        // Per-team score-up arrows — admin-only. Non-admin viewers see the
        // scoreboard without any controls so they can't tamper with the live
        // score from the public detail page.
        if (isAdmin) {
            Column(
                modifier = Modifier.sPaddingTop(4).sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                event.teams.forEach { team ->
                    Box(
                        modifier = Modifier.sHeight(26),
                        contentAlignment = Alignment.Center,
                    ) {
                        ScoreArrowButton(
                            onIncrement = { onScoreUp(team.id) },
                            onDecrement = { onScoreDown(team.id) },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ScoreArrowButton(
    onIncrement: () -> Unit,
    onDecrement: () -> Unit,
) {
    // Container: 24x34 visible as a rotated -90deg 34x24 button. Tapping
    // the arrow no longer increments directly — it toggles the `+/−`
    // stepper pill (`ic_add_stat.png`, same asset as the StatBlock
    // pop-outs on player cards). Top half adds 1 to the team score,
    // bottom half subtracts 1.
    var open by remember { mutableStateOf(false) }
    Box(
        modifier = Modifier
            .sWidth(24)
            .sHeight(34)
            .position(Position.Relative),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sWidth(34)
                .sHeight(24)
                .cursor(Cursor.Pointer)
                .onClick {
                    it.stopPropagation()
                    open = !open
                }
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border-radius", "0 0 ${s(8)} ${s(8)}")
                    property("background", "linear-gradient(to top, rgba(12,66,255,0.20) 3.12%, rgba(102,102,102,0) 60.12%)")
                    property("box-shadow", "inset 0 -0.5px 0 0 rgba(255,255,255,0.15), inset -0.5px 0 0 0 rgba(255,255,255,0.15), inset 0.5px 0 0 0 rgba(255,255,255,0.15)")
                    // Closed → chevron points OUTWARD (right) inviting a
                    // tap; open → flips 180° so it points INWARD (left)
                    // hinting that the next tap closes the pill.
                    val rotation = if (open) "rotate(90deg)" else "rotate(-90deg)"
                    property("transform", rotation)
                    property("transition", "transform 0.3s ease-out")
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.ChevronDown,
                modifier = Modifier.sSize(16).styleModifier {
                    property("filter", "brightness(0) invert(1)")
                }
            )
        }

        // Stepper pill viewport — always mounted so the slide-in / slide-out
        // animation runs in both directions. The viewport stays pinned to
        // the arrow's right side with `overflow: hidden`; the inner pill
        // translates from `-100%` (tucked behind the arrow) to `0`
        // (visible) on open and reverses on close.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", "100%")
                    property("top", "50%")
                    property("transform", "translateY(-50%)")
                    property("margin-left", s(4))
                    property("width", s(34))
                    property("height", s(76))
                    property("overflow", "hidden")
                    property("z-index", "1000")
                    property("pointer-events", if (open) "auto" else "none")
                }
        ) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "0")
                        property("left", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property(
                            "transform",
                            if (open) "translateX(0)" else "translateX(-100%)",
                        )
                        property("opacity", if (open) "1" else "0")
                        property(
                            "transition",
                            "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)," +
                                " opacity 0.35s ease",
                        )
                        property("background", "url('/icons/ic_add_stat.png') center/contain no-repeat")
                    }
            ) {
                // Top half — `+1` to team score.
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("top", "0")
                            property("left", "0")
                            property("right", "0")
                            property("height", "50%")
                        }
                        .cursor(Cursor.Pointer)
                        .onClick {
                            it.stopPropagation()
                            onIncrement()
                        }
                )
                // Bottom half — `−1`. Backend's score endpoint clamps at
                // zero so we don't need to gate here.
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("bottom", "0")
                            property("left", "0")
                            property("right", "0")
                            property("height", "50%")
                        }
                        .cursor(Cursor.Pointer)
                        .onClick {
                            it.stopPropagation()
                            onDecrement()
                        }
                )
            }
        }
    }
}

// ── Pro Stat+ Toggle ──

@Composable
private fun ProStatToggle(
    enabled: Boolean,
    onToggle: () -> Unit
) {
    val t = LocalAppStrings.current
    Column(
        horizontalAlignment = Alignment.End
    ) {
        // Main pill
        Row(
            modifier = Modifier
                .sHeight(48)
                .sBorderRadius(34)
                .sGap(16)
                .styleModifier {
                    property("padding-left", s(18))
                    property("padding-right", s(4))
                    property("background", AppGradients.InteractiveSecondary)
                    property("box-shadow", "inset 0 0 0 0.5px rgba(255,255,255,0.15)")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Label
            Column {
                Row(modifier = Modifier.sGap(2)) {
                    SpanText(
                        t.eventProLabel,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(FontWeight.Normal)
                            .color(Color.rgb(30, 127, 245))
                            .styleModifier { property("line-height", s(16)) }
                    )
                    SpanText(
                        "+",
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(FontWeight.Medium)
                            .color(Color.rgb(30, 127, 245))
                            .styleModifier { property("line-height", s(16)) }
                    )
                }
                SpanText(
                    t.eventStatLabel,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Normal)
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("line-height", s(16)) }
                )
            }

            // Toggle switch wrapper
            Row(
                modifier = Modifier
                    .sHeight(44)
                    .sBorderRadius(34)
                    .overflow(Overflow.Hidden)
                    .styleModifier {
                        property("padding-right", s(4))
                        property("padding-top", s(2))
                        property("padding-bottom", s(2))
                    },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.End
            ) {
                Box(
                    modifier = Modifier
                        .sWidth(51)
                        .sHeight(31)
                        .sBorderRadius(16)
                        .cursor(Cursor.Pointer)
                        .onClick { onToggle() }
                        .position(Position.Relative)
                        .styleModifier {
                            property("background", if (enabled) "#1e7ff5" else "#3a3a3c")
                            property("transition", "background 0.2s")
                        }
                ) {
                    // Knob
                    Box(
                        modifier = Modifier
                            .sSize(27)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.PrimaryWhite)
                            .position(Position.Absolute)
                            .styleModifier {
                                property("top", "2px")
                                property("left", if (enabled) s(22) else "2px")
                                property("transition", "left 0.2s")
                                property("box-shadow", "0 2px 4px rgba(0,0,0,0.3)")
                            }
                    )
                }
            }
        }

        // Expand/collapse chevron — offset right by 21px to align under toggle
        Box(
            modifier = Modifier.styleModifier {
                property("padding-right", s(21))
            }
        ) {
            Box(
                modifier = Modifier
                    .sWidth(51)
                    .sHeight(24)
                    .styleModifier {
                        property("border-radius", "0 0 ${s(12)} ${s(12)}")
                        property("background", AppGradients.InteractiveSecondary)
                        property("box-shadow", "inset 0 -0.5px 0 0 rgba(255,255,255,0.15), inset -0.5px 0 0 0 rgba(255,255,255,0.15), inset 0.5px 0 0 0 rgba(255,255,255,0.15)")
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.ChevronDown,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", "brightness(0) invert(1)")
                    }
                )
            }
        }
    }
}

// ── End Match Button ──

@Composable
private fun EndMatchButton(onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(22)
            .sPadding(8, 22)
            .backgroundColor(Color.rgb(198, 89, 12))
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            t.eventEndMatch,
            modifier = Modifier
                .bodyLMedium()
                .color(AppColors.PrimaryWhite)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// ── Timeline Button ──

@Composable
private fun TimelineButton(onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(28)
            .backgroundColor(Color.rgb(0, 229, 164))
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sGap(12)
            .styleModifier {
                property("padding-left", s(4))
                property("padding-right", s(18))
                property("padding-top", s(4))
                property("padding-bottom", s(4))
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icon circle
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .backgroundColor(Color.rgb(0, 194, 136))
                .overflow(Overflow.Hidden),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Analytics,
                modifier = Modifier.sSize(24)
            )
        }

        SpanText(
            t.eventTimeline,
            modifier = Modifier
                .bodyLMedium()
                .color(AppColors.PrimaryBlack)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// ── Field Formation Area ──

@Composable
private fun FieldFormationArea(
    event: EventDetailDto,
    visibleTeams: List<EventTeamDto>,
    onCycleTeamSlot: (slotIdx: Int) -> Unit,
    isAdmin: Boolean,
    effectiveFormat: Int,
    slotPlayerOverrides: Map<String, String?>,
    selectedSlotKey: String?,
    participantById: Map<String, EventParticipantDto>,
    playerStats: EventPlayerStatsResultDto?,
    onSetPlayerStat: (userId: String, statTypeId: String, value: Int) -> Unit,
    onTapSlot: (slotKey: String, currentPlayer: EventParticipantDto?) -> Unit,
    onViewProfile: (String) -> Unit,
    forceShowTeamArrows: Boolean = false,
    // Forwarded to `PlayerCardKit` so Pro completed Lineups can surface
    // the gold-star + numeric rating overlay; live IN_PROGRESS keeps it
    // off via the default.
    showRating: Boolean = false,
    // Forwarded to `PlayerCardKit` to drop corner StatBlocks with a
    // 0 count from the Pro completed Lineups view.
    hideZeroStats: Boolean = false,
    // Forwarded to `PlayerCardKit` — Lineups taps open the Rate
    // Performance popup; live IN_PROGRESS leaves this null and the
    // outer slot wrapper handles admin swap taps instead.
    onPlayerClick: ((EventParticipantDto) -> Unit)? = null,
    // Per-userId event-scoped community ★ average; the FieldFormationArea
    // looks up the player's value here and forwards it into the
    // `PlayerCardKit`'s rating overlay.
    eventRatingAverages: Map<String, Double> = emptyMap(),
) {
    // Always use the same intrinsic card geometry (`XtraSmall`); the visual
    // shrink for bigger formats is done via the CSS `scale()` factor in
    // [cardScaleForFormat] so the layout maths (Y-band, default positions)
    // doesn't need to change per format.
    val cardSize = PlayerCardSize.XtraSmall
    val cardScale = cardScaleForFormat(effectiveFormat)

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .position(Position.Relative)
            .styleModifier { property("overflow", "visible") }
    ) {
        // Team A (left half) and Team B (right half). Pulled from
        // `visibleTeams` so events with 3+ teams can cycle which two are
        // on screen via the chevron under each crest.
        visibleTeams.forEachIndexed { teamIdx, team ->
            val isLeftTeam = teamIdx == 0

            val formationName = team.formation?.name ?: defaultFormationForFormat(effectiveFormat)
            val rows = parseFormation(formationName)

            val confirmed = event.participants
                .filter { it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED" }
            val fieldPlayers = confirmed.take(effectiveFormat)
            val assigned = assignPlayersToFormation(fieldPlayers, rows)
            val rowXPercents = rowXPercentsForTeam(rows.size, isLeftTeam)

            rows.forEachIndexed { rowIdx, count ->
                val defaultX = rowXPercents.getOrElse(rowIdx) { 25.0 }
                for (col in 0 until count) {
                    // Formation hugs the top of the field (0%) and stops
                    // at ~45% — the rest of the field is reserved for the
                    // separate substitutes row that sits below. Multi-player
                    // rows distribute evenly across 0%→45%; a single player
                    // sits in the middle of it.
                    val defaultY = if (count == 1) 22.0
                    else col.toDouble() / (count - 1) * 45.0

                    val slotKey = "$teamIdx-$rowIdx-$col"
                    // Per-format hand-tuned defaults (left team only, mirrored
                    // for the right team via `100 - x`). Falls back to the
                    // auto formula above if no default exists for this slot.
                    val leftSlotKey = "0-$rowIdx-$col"
                    val perFormatDefault = defaultPositionsForFormat(effectiveFormat)[leftSlotKey]
                        ?.let { (lx, ly) ->
                            if (teamIdx == 0) (lx to ly) else ((100.0 - lx) to ly)
                        }
                    val finalX = perFormatDefault?.first ?: defaultX
                    val finalY = perFormatDefault?.second ?: defaultY

                    val defaultPlayer = assigned.getOrNull(rowIdx)?.getOrNull(col)
                    // Resolve the current player at this slot: an explicit
                    // override (from a prior swap) wins; otherwise the
                    // formation-assigned default is used. A `null` override
                    // value means "explicitly empty" (e.g. a bench player
                    // was swapped here from an empty slot).
                    val player = if (slotPlayerOverrides.containsKey(slotKey)) {
                        slotPlayerOverrides[slotKey]?.let { participantById[it] }
                    } else {
                        defaultPlayer
                    }
                    val isSelected = selectedSlotKey == slotKey
                    // Compose-runtime key so the SAME DOM element is reused
                    // across renders for a given player ID. Swapping the
                    // contents of two slots becomes a `left`/`top` change on
                    // each card's underlying DOM, which CSS transitions then
                    // animate as smooth movement. Empty slots key by slotKey
                    // so the placeholder stays put.
                    key(player?.id ?: "empty-$slotKey") {

                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .styleModifier {
                                property("left", "$finalX%")
                                property("top", "$finalY%")
                                // Position (`left`/`top`) animates during the
                                // swap; `transform`/`filter` animate the
                                // selected-state pop-out + glow. All four
                                // share the same eased duration so the swap
                                // reads as one coordinated motion.
                                val selectScale = if (isSelected) "1.1" else "1.0"
                                property(
                                    "transform",
                                    "translate(-50%, -50%) scale($selectScale)",
                                )
                                property(
                                    "transition",
                                    // The swap itself IS the `left`/`top`
                                    // movement — the two picked cards glide
                                    // across the field to exchange positions
                                    // at 2.0s so the trajectory is clearly
                                    // visible. Selection feedback (`transform`
                                    // scale + `filter` glow) stays snappy at
                                    // 0.2s so taps land instantly.
                                    "left 2.0s cubic-bezier(0.22, 1, 0.36, 1)," +
                                        " top 2.0s cubic-bezier(0.22, 1, 0.36, 1)," +
                                        " transform 0.2s ease-out," +
                                        " filter 0.2s ease-out",
                                )
                                if (isSelected) {
                                    property(
                                        "filter",
                                        "drop-shadow(0 0 14px rgba(60,121,241,0.85))" +
                                            " drop-shadow(0 0 4px rgba(255,255,255,0.6))",
                                    )
                                }
                                property(
                                    "z-index",
                                    if (isSelected) "1000" else "${50 + (rows.size - rowIdx)}",
                                )
                                if (isAdmin) {
                                    property("cursor", "pointer")
                                    property("user-select", "none")
                                    property("-webkit-user-select", "none")
                                }
                            }
                            .then(
                                if (isAdmin) Modifier.onClick {
                                    it.stopPropagation()
                                    onTapSlot(slotKey, player)
                                } else Modifier
                            )
                    ) {
                        if (player != null) {
                            PlayerCardKit(
                                player = player,
                                cardSize = cardSize,
                                cardScale = cardScale,
                                sport = event.sportName,
                                // Stat tiles are always rendered on the live
                                // match field — the `extendedStatsEnabled`
                                // toggle has been retired in favour of the
                                // sport-aware StatBlock contents (e.g. tennis
                                // 0/15/30/40). The four corner blocks are now
                                // a core part of the match-view player card.
                                showStatBlocks = true,
                                isAdmin = isAdmin,
                                playerStats = playerStats,
                                onSetPlayerStat = onSetPlayerStat,
                                showRating = showRating,
                                hideZeroStats = hideZeroStats,
                                onPlayerClick = onPlayerClick,
                                eventRatingAverage = eventRatingAverages[player.userId],
                            )
                        } else {
                            // Empty slot — render the blank football-card
                            // art from R2 (`football_card.png`) at the same
                            // footprint as a real card, dimmed via
                            // `filter: brightness(0.35)` so the slot reads
                            // as inactive but still matches the visual
                            // weight of the surrounding player cards.
                            Box(
                                modifier = Modifier
                                    .sWidth(cardSize.w).sHeight(cardSize.h)
                                    .styleModifier {
                                        property("transform", "scale($cardScale)")
                                        property("transform-origin", "center")
                                        property("filter", "brightness(0.35)")
                                        property(
                                            "background",
                                            "url('${PLAYER_KIT_CDN_BASE}football_card.png')" +
                                                " center/contain no-repeat",
                                        )
                                    }
                            )
                        }
                    }
                    } // close key(...)
                }
            }

            // Team crest at left/right edge, vertically aligned with the
            // formation centre (≈22%, half of the 0–45% band).
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        if (isLeftTeam) property("left", "0") else property("right", "0")
                        property("top", "22%")
                        property("transform", "translateY(-50%)")
                    }
            ) {
                TeamCrestSide(
                    team = team,
                    // Arrow appears whenever there's a different team to
                    // cycle to. In the live match view this mutates the
                    // backend display order (admin-only), but in the
                    // completed Lineups view it just rotates the visible
                    // pair locally — `forceShowTeamArrows` opts viewers
                    // into the latter.
                    showArrow = (isAdmin || forceShowTeamArrows) && event.teams.size > 2,
                    onArrowClick = { onCycleTeamSlot(teamIdx) },
                )
            }
        }
    }
}

@Composable
private fun FormationToolbar(
    overrideFormat: Int?,
    onFormatChange: (Int?) -> Unit,
) {
    // Rendered inline inside `TopBar`'s right-cluster row, no absolute
    // positioning — sits flush left of the End Match button. "Auto" defers
    // to the event's real format; the numeric pills override it locally
    // (useful for previewing layouts at other team sizes).
    val t = LocalAppStrings.current
    Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.CenterVertically) {
        FormatPill(t.eventFormatAuto, selected = overrideFormat == null) { onFormatChange(null) }
        (1..11).forEach { fmt ->
            FormatPill(
                label = "${fmt}×${fmt}",
                selected = overrideFormat == fmt,
            ) { onFormatChange(fmt) }
        }
    }
}

@Composable
private fun FormatPill(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sPadding(4, 10)
            .sBorderRadius(12)
            .styleModifier {
                property(
                    "background",
                    if (selected) "rgba(60, 121, 241, 0.45)" else "rgba(0, 0, 0, 0.5)",
                )
                property(
                    "border",
                    if (selected) "1px solid rgba(190, 214, 245, 0.55)"
                    else "1px solid rgba(190, 214, 245, 0.15)",
                )
                property("cursor", "pointer")
                property("user-select", "none")
            }
            .onClick {
                it.stopPropagation()
                onClick()
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(10)
                .fontWeight(FontWeight.Medium)
                .color(AppColors.Text.Primary),
        )
    }
}

// ── Player Card Kit ──

private const val PLAYER_KIT_CDN_BASE = "https://images.arenaproplay.com/"

/**
 * Player card "kit" used on the formation field. Wraps [PlayerCard] in a
 * clickable hit-target and, when [showStatBlocks] is on, mounts four
 * `stat_block.png` frames (target/ball/yellow/red) at the corners — Figma
 * 7904:364601. Each block, when tapped by an admin, slides out two extra
 * `stat_block_top.png` / `stat_block_bottom.png` panels above/below for the
 * `+` and `−` increments. Non-admin viewers see the blocks read-only.
 */
@Composable
internal fun PlayerCardKit(
    player: EventParticipantDto,
    cardSize: PlayerCardSize,
    cardScale: Double,
    sport: String,
    showStatBlocks: Boolean,
    isAdmin: Boolean,
    playerStats: EventPlayerStatsResultDto? = null,
    onSetPlayerStat: (userId: String, statTypeId: String, value: Int) -> Unit =
        { _, _, _ -> },
    // Pro completed Lineups overlays a gold star + the player's overall
    // rating in the upper-centre of each card (Figma 7906:378910 +
    // 7906:379049). Live match / in-progress cards leave it off — stat
    // blocks already crowd the corners and the rating shield is hidden
    // via PlayerCard's `hideRating = true`.
    showRating: Boolean = false,
    // When true, any of the four corner StatBlocks whose current count is
    // 0 is hidden — used by the Pro completed Lineups view to clean up
    // cards for players who never accrued a given stat. Live match still
    // wants `0`-valued blocks visible so admins can increment them, so
    // the default keeps the old behaviour.
    hideZeroStats: Boolean = false,
    // Lineups taps invoke this with the underlying participant so the
    // surrounding view can open the Rate Performance popup. Null in live
    // IN_PROGRESS — admin swap clicks live on the slot wrapper above
    // PlayerCardKit instead.
    onPlayerClick: ((EventParticipantDto) -> Unit)? = null,
    // Event-scoped community star rating (1.0–5.0) — average of every
    // reviewer's `generalRating` for this player in this event. When
    // null we fall back to the participant's all-events OVR via
    // `player.overallRating`, but the Lineups view passes the
    // event-only average so the ★ overlay matches what the popup's
    // Community Rating block shows.
    eventRatingAverage: Double? = null,
) {
    // Field cards land tiny by default (XtraSmall = 58×90). The CSS scale
    // grows the rendered footprint to match the Figma reference; bigger
    // formats use a smaller scale ([cardScaleForFormat]) so 10+ players
    // per side still fit comfortably without colliding. We scale instead
    // of bumping `PlayerCardSize` so the layout maths in
    // `FieldFormationArea` (percentage anchors, translate(-50%, -50%))
    // stays intact — the kit is centred on its anchor and grows outward
    // from there. The card itself isn't clickable — profile navigation
    // happens elsewhere; only the per-stat tiles take taps (admin only).
    Box(
        // Explicit `width`/`height` pinned to the card's intrinsic
        // `cardSize` so the four corner `StatBlock`s can resolve their
        // percentage-based `left`/`right`/`top`/`bottom` against a stable
        // box. Without this the wrapper would shrink-wrap PlayerCard and
        // — on some Kobweb Box display modes — only the slot with `left`
        // would land at a meaningful coordinate (the others used `right`
        // or `bottom`, which need an explicit reference dimension).
        modifier = Modifier
            .sWidth(cardSize.w).sHeight(cardSize.h)
            .position(Position.Relative)
            .styleModifier {
                property("transform", "scale($cardScale)")
                property("transform-origin", "center")
            }
            .then(
                if (onPlayerClick != null) {
                    Modifier.cursor(Cursor.Pointer).onClick {
                        it.stopPropagation()
                        onPlayerClick(player)
                    }
                } else Modifier
            )
    ) {
        val resolvedName = (player.fullName?.split(" ")?.lastOrNull()
            ?: player.username).uppercase()
        PlayerCard(
            // Pro Lineups (`showRating = true`) suppresses the card's
            // built-in name slot — `PlayerInfoBlock` sits low on XtraSmall
            // and would overlap the bottom-centre rating badge we draw
            // below. The name is instead re-rendered manually further up,
            // anchored above the badge.
            playerName = if (showRating) "" else resolvedName,
            rating = player.overallRating.toInt(),
            position = player.position ?: "",
            sport = sport,
            size = cardSize,
            avatarUrl = player.cardAvatarUrl ?: AppIcons.EmptyAvatar,
            cardAssetsBasePath = player.cardAssetsBasePath,
            cardTextColor = player.cardTextColor,
            // Live match cards surface stats via the four corner StatBlocks
            // — keeping the rating shield on top of that would crowd the
            // small footprint and duplicate the "score" notion, so we hide
            // it here.
            hideRating = true,
        )

        // `other` sport events skip stat blocks entirely — they're casual
        // sessions with no structured scoring, and the match view itself
        // never opens for them (start jumps straight to COMPLETED).
        if (showStatBlocks && sport.lowercase() != "other") {
            val basePath = player.cardAssetsBasePath ?: "cards/football"
            val sportLower = sport.lowercase()
            // Tennis and padel both use the 0/15/30/40/Ad point progression
            // on the top-left block, and both reset that counter to zero
            // when the top-right ("games") block is incremented.
            val isPointGameSport = sportLower in listOf("tennis", "padel")
            val topLeftFormatter: (Int) -> String = if (isPointGameSport) {
                { c ->
                    when (c) {
                        0 -> "0"
                        1 -> "15"
                        2 -> "30"
                        3 -> "40"
                        // Beyond 40 we keep incrementing numerically — 41,
                        // 42, 43… — so admins can rack up tie-break style
                        // points without the counter freezing on `Ad`.
                        else -> (c + 37).toString()
                    }
                }
            } else {
                { it.toString() }
            }

            // Icon placement:
            //   • tennis / padel: target (points) on LEFT, ball (games) on RIGHT
            //   • football / basketball / volleyball / other: assists (ball)
            //     on LEFT, goals (target) on RIGHT
            val topLeftIcon = if (isPointGameSport) AppIcons.StatBlockTarget else AppIcons.StatBlockBall
            val topLeftIconSize: Number = if (isPointGameSport) 6 else 5
            val topRightIcon = if (isPointGameSport) AppIcons.StatBlockBall else AppIcons.StatBlockTarget
            val topRightIconSize: Number = if (isPointGameSport) 5 else 6

            // Per Figma 7904:364601: 28×44 tile on a 130×202 card ⇒ ~22% × 22%.
            // Right-side tiles mirror the left horizontally so the notch
            // faces the card centre on both sides. Inner content is un-
            // mirrored.
            //
            // Counter values come from `playerStats` (live backend snapshot
            // — see `EventDetailViewModel.loadPlayerStats()`). Lookup is
            // `displayKey → SportStatTypeDto.id → EventPlayerStatDto.value`
            // (or `0` when the player hasn't accrued the stat yet). Taps
            // call `onSetPlayerStat(userId, statTypeId, newValue)` which
            // POSTs the bulk update and triggers a `STAT_UPDATE` broadcast
            // for every connected viewer.
            val blockKeys = statBlockKeysForSport(sport)
            val statTypeByKey: Map<String, SportStatTypeDto> =
                playerStats?.statTypes?.associateBy { st -> st.displayKey } ?: emptyMap()
            val playerStatByTypeId: Map<String, Int> =
                playerStats?.playerStats
                    ?.filter { it.userId == player.userId }
                    ?.associate { it.statTypeId to it.value }
                    ?: emptyMap()

            fun typeIdForKey(key: String?): String? {
                if (key == null) return null
                val stat: SportStatTypeDto = statTypeByKey[key] ?: return null
                return stat.id
            }

            fun countForKey(key: String?): Int {
                val typeId = typeIdForKey(key) ?: return 0
                return playerStatByTypeId[typeId] ?: 0
            }
            fun push(key: String?, delta: Int) {
                val typeId = typeIdForKey(key) ?: return
                val current = playerStatByTypeId[typeId] ?: 0
                val next = (current + delta).coerceAtLeast(0)
                if (next == current) return
                onSetPlayerStat(player.userId, typeId, next)
            }

            val tlKey = blockKeys.getOrNull(0)
            val trKey = blockKeys.getOrNull(1)
            val blKey = blockKeys.getOrNull(2)
            val brKey = blockKeys.getOrNull(3)

            val topLeftCount = countForKey(tlKey)
            val topRightCount = countForKey(trKey)
            val bottomLeftCount = countForKey(blKey)
            val bottomRightCount = countForKey(brKey)

            // Top-left: tennis/padel → point (0/15/30/40/Ad…); others → assists.
            if (!hideZeroStats || topLeftCount > 0) {
                StatBlock(
                    icon = topLeftIcon,
                    iconSize = topLeftIconSize,
                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                    count = topLeftCount,
                    onIncrement = { push(tlKey, +1) },
                    onDecrement = { push(tlKey, -1) },
                    top = "18%", left = "5%", flipHorizontally = false,
                    valueFormatter = topLeftFormatter,
                )
            }
            // Top-right: tennis/padel → games (incrementing resets top-left
            // points to 0 on the same `setPlayerStats` round-trip); others →
            // goals.
            if (!hideZeroStats || topRightCount > 0) {
                StatBlock(
                    icon = topRightIcon,
                    iconSize = topRightIconSize,
                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                    count = topRightCount,
                    onIncrement = {
                        push(trKey, +1)
                        if (isPointGameSport) {
                            val tlTypeId = typeIdForKey(tlKey)
                            if (tlTypeId != null && topLeftCount != 0) {
                                onSetPlayerStat(player.userId, tlTypeId, 0)
                            }
                        }
                    },
                    onDecrement = { push(trKey, -1) },
                    top = "18%", right = "5%", flipHorizontally = true,
                )
            }
            if (!hideZeroStats || bottomLeftCount > 0) {
                StatBlock(
                    iconTint = "#FFFF54",
                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                    count = bottomLeftCount,
                    onIncrement = { push(blKey, +1) },
                    onDecrement = { push(blKey, -1) },
                    bottom = "22%", left = "5%", flipHorizontally = false,
                )
            }
            if (!hideZeroStats || bottomRightCount > 0) {
                StatBlock(
                    iconTint = "#FF3737",
                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                    count = bottomRightCount,
                    onIncrement = { push(brKey, +1) },
                    onDecrement = { push(brKey, -1) },
                    bottom = "22%", right = "5%", flipHorizontally = true,
                )
            }
        }

        // Star + 1-decimal rating overlay for Pro Lineups (Figma
        // 7906:378910 / 7906:379049). Tucked into the upper-right corner.
        // PLUS a bottom-centre `card_rating.png` badge replicating the
        // registration-flow Arena layout — the FIFA-style overall shield
        // sits below the player silhouette so each Lineups card carries
        // both the event-specific star rating (top-right) and the
        // player's overall card rating (bottom-centre).
        if (showRating) {
            val rawRating = eventRatingAverage ?: player.overallRating
            val ratingText = (kotlin.math.round(rawRating * 10) / 10.0).toString()
            // Top-right star + decimal rating.
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sGap(1)
                    .styleModifier {
                        property("top", "13%")
                        property("right", "22%")
                        property("z-index", "5")
                        property("pointer-events", "none")
                        property("align-items", "center")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .sSize(8)
                        .styleModifier {
                            property("background-color", "#F2B73D")
                            property("mask-image", "url('${AppIcons.StarRating16}')")
                            property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                            property("mask-size", "contain")
                            property("-webkit-mask-size", "contain")
                            property("mask-position", "center")
                            property("-webkit-mask-position", "center")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-repeat", "no-repeat")
                        }
                )
                // Digit wrapped in a same-height Box with explicit
                // flex-centring so the glyph's visual middle sits
                // exactly on the star's centre — `inline-flex` on the
                // span alone leaves the digit baseline-anchored.
                Box(
                    modifier = Modifier
                        .sHeight(8)
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                        },
                ) {
                    SpanText(
                        ratingText,
                        modifier = Modifier
                            .sFontSize(8)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.Text.Secondary)
                            .styleModifier {
                                property("line-height", "1")
                                property("font-family", "Urbanist, sans-serif")
                            }
                    )
                }
            }

            // Bottom-centre `card_rating.png` badge — mirrors the
            // registration flow's Arena card layout so the field card
            // surfaces the player's overall FIFA rating in the same
            // spot the user picked it in. Skipped when the player has
            // no per-asset path (default avatar fallback would have no
            // matching badge image).
            if (!player.cardAssetsBasePath.isNullOrBlank()) {
                val sportColors = SportColors.forSport(sport)
                val effectiveColor = player.cardTextColor?.takeIf { it.isNotBlank() }
                    ?.let { hex ->
                        val cleaned = hex.removePrefix("#")
                        com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
                            cleaned.substring(0, 2).toInt(16),
                            cleaned.substring(2, 4).toInt(16),
                            cleaned.substring(4, 6).toInt(16),
                        )
                    } ?: sportColors.primary
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .sWidth(14).sHeight(22)
                        .styleModifier {
                            property("bottom", "8%")
                            property("left", "50%")
                            property("transform", "translateX(-50%)")
                            property("z-index", "4")
                            property("pointer-events", "none")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Image(
                        src = "${PLAYER_KIT_CDN_BASE}${player.cardAssetsBasePath}/card_rating.png",
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("position", "absolute")
                                property("pointer-events", "none")
                            }
                    )
                    SpanText(
                        "${player.overallRating.toInt()}",
                        modifier = Modifier
                            .sFontSize(6)
                            .fontWeight(FontWeight.SemiBold)
                            .color(effectiveColor)
                            .styleModifier {
                                property("line-height", "1")
                                property("font-family", "Urbanist, sans-serif")
                                property("z-index", "1")
                            }
                    )
                }
            }

            // Player surname rendered manually so we can park it directly
            // above the bottom-centre rating badge. PlayerCard's built-in
            // `PlayerInfoBlock` for XtraSmall sits at y≈71 of the 90-px
            // card — flush with the rating badge below — so we suppress
            // it via `playerName = ""` above and re-place the name here at
            // ~58% from the top, just above the rating chrome.
            SpanText(
                resolvedName,
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "58%")
                        property("left", "50%")
                        property("transform", "translateX(-50%)")
                        property("z-index", "4")
                        property("pointer-events", "none")
                        property("white-space", "nowrap")
                        property("text-align", "center")
                        property("font-family", "Urbanist, sans-serif")
                    }
                    .sFontSize(6)
                    .fontWeight(FontWeight.Bold)
                    .color(effectiveSportColor(sport, player.cardTextColor))
            )
        }
    }
}

/**
 * Resolves the surname tint for the manual Lineups name overlay. Mirrors
 * the same logic the FIFA card art uses (`cardTextColor` override falls
 * back to the sport's primary), so the name reads in the player's
 * card-template palette rather than the default Arena blue.
 */
private fun effectiveSportColor(
    sport: String,
    cardTextColor: String?,
): com.varabyte.kobweb.compose.ui.graphics.Color {
    val sc = SportColors.forSport(sport)
    val hex = cardTextColor?.takeIf { it.isNotBlank() } ?: return sc.primary
    val cleaned = hex.removePrefix("#")
    return com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
        cleaned.substring(0, 2).toInt(16),
        cleaned.substring(2, 4).toInt(16),
        cleaned.substring(4, 6).toInt(16),
    )
}

/**
 * Single corner stat block (Figma 7904:364601 — 28×44 tile). Background is the
 * R2 `stat.png` frame; icon + counter are drawn over it via code.
 *
 * Tap (admin only) toggles `stat_add_top.png` above (`+`) and
 * `stat_add_bottom.png` below (`−`) — both are single stepper pills (~28×28
 * each) wired to [onIncrement] / [onDecrement].
 */
@Composable
internal fun StatBlock(
    icon: String? = null,
    iconTint: String? = null,
    iconSize: Number = 6,
    isAdmin: Boolean,
    cardAssetsBasePath: String,
    // Counter state is owned by the caller — letting the parent (PlayerCardKit)
    // coordinate cross-block side-effects like "tennis games +1 resets points
    // to 0". The block just renders whatever `count` it's handed and asks the
    // caller to increment/decrement through the callbacks.
    count: Int,
    onIncrement: () -> Unit,
    onDecrement: () -> Unit,
    top: String? = null,
    bottom: String? = null,
    left: String? = null,
    right: String? = null,
    flipHorizontally: Boolean = false,
    // Lets the caller render the counter using non-numeric labels (e.g.
    // the tennis 0/15/30/40 convention for the points block). Defaults to
    // the integer count itself.
    valueFormatter: (Int) -> String = { it.toString() },
    // Mobile action sheet shows every +/− pill from the start (no
    // tap-to-reveal); desktop keeps the default toggle behaviour. Only
    // applies for admins — viewers never get steppers either way.
    steppersAlwaysOpen: Boolean = false,
    // How far past the tile's outer edge the +/− pop-out sits, as % of the
    // tile width. 100 = flush against the tile (desktop default); larger
    // values push the pill further out to the side.
    stepperOutsetPct: Int = 100,
) {
    var open by remember { mutableStateOf(false) }
    val effectiveOpen = open || (steppersAlwaysOpen && isAdmin)
    val blockUrl = "${PLAYER_KIT_CDN_BASE}${cardAssetsBasePath}/stat.png"
    // R2 half-pill is the primary pop-out asset (top tiles use the `+`
    // variant, bottom tiles the `−` variant) and drives the tap action.
    // `ic_add_stat.png` is a local Figma overlay that sits on top of the R2
    // pill, slightly inset, providing the polished +/− glyphs/gradient from
    // the latest design without changing the click semantics.
    val addTopUrl = "${PLAYER_KIT_CDN_BASE}${cardAssetsBasePath}/stat_add_top.png"
    val addBottomUrl = "${PLAYER_KIT_CDN_BASE}${cardAssetsBasePath}/stat_add_bottom.png"
    val stepperOverlayUrl = "/icons/ic_add_stat.png"

    // Wrapper sized to the stat-tile footprint (22% × 22% of the card area
    // ≈ Figma 28×44 on a 130×202 card). NB: the `scaleX(-1)` mirror for
    // right-side tiles is applied to the inner `stat.png` frame below, NOT
    // to this wrapper — flipping the wrapper would also flip the pop-out
    // child coordinates, sending right-tile pop-outs to the visual left.
    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .styleModifier {
                if (top != null) property("top", top)
                if (bottom != null) property("bottom", bottom)
                if (left != null) property("left", left)
                if (right != null) property("right", right)
                property("width", "22%")
                property("height", "28%")
                property("z-index", "5")
            }
    ) {
        // Per Figma 7904:364601 the steppers sit OUTSIDE the card on the
        // matching side (left for left-tile, right for right-tile), stacked
        // as a vertical pill with `+` on top half and `−` on bottom half.
        // We infer the side from which positioning prop the caller passed —
        // `left` means the tile is on the LEFT, so the pop-out shifts LEFT
        // (out via `right: 100%`); same logic mirrored for the right side.
        val popSide = if (right != null) "left" else "right"

        // R2 half-pill is the visual backdrop (variant chooses `+` or `−`
        // asset), and `ic_add_stat.png` overlays the polished Figma pill on
        // top. Click logic lives on the overlay's two halves: top half →
        // `+1`, bottom half → `−1` (clamped at 0).
        //
        // Animation: the outer `viewport` Box sits at the pop-out's OPEN
        // position with `overflow: hidden`. Inside it the pop-out slides on
        // the X axis — closed state pushes it 100% past the card-facing edge,
        // so the viewport clips the portion that would otherwise overlap the
        // card. The visual effect: the pill emerges from under the card edge
        // when opening, and tucks back under when closing. Pointer events are
        // gated on `open` so a closed (clipped) pop-out can't catch taps.
        val isTopVariant = top != null
        val popUrl = if (isTopVariant) addTopUrl else addBottomUrl
        // Closed-state slide: tuck the pop-out toward the card centre. For
        // left tiles (popSide=right → pop-out emerges leftward), tucking
        // means +X; for right tiles, the mirror.
        val closedSlideX = if (popSide == "right") "100%" else "-100%"
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property(popSide, "$stepperOutsetPct%")
                    // Bottom variant sits lower than the top variant — its
                    // vertical centre shifts past the tile's centre so the
                    // `−` pill hangs visibly below the tile, not on top
                    // of it.
                    property("top", if (isTopVariant) "25%" else "105%")
                    property("transform", "translateY(-50%)")
                    property("width", "180%")
                    property("height", "200%")
                    property("overflow", "hidden")
                    property("pointer-events", if (effectiveOpen) "auto" else "none")
                }
        ) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "0")
                        property("left", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property(
                            "transform",
                            if (effectiveOpen) "translateX(0)" else "translateX($closedSlideX)",
                        )
                        property("background", "url('$popUrl') center/contain no-repeat")
                        property("opacity", if (effectiveOpen) "1" else "0")
                        property(
                            "transition",
                            "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease",
                        )
                    }
                    .onClick {
                        it.stopPropagation()
                        open = false
                    },
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "relative")
                            property("width", "63%")
                            property("height", "63%")
                            property("background", "url('$stepperOverlayUrl') center/contain no-repeat")
                        }
                ) {
                    // Top half of `add_stat.png` — `+1`. Pop-out stays open
                    // so admins can rack up multiple goals/etc. in a row;
                    // tap the stat tile again (or anywhere outside the
                    // overlay) to dismiss.
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .styleModifier {
                                property("top", "0")
                                property("left", "0")
                                property("right", "0")
                                property("height", "50%")
                            }
                            .cursor(Cursor.Pointer)
                            .onClick {
                                it.stopPropagation()
                                onIncrement()
                            }
                    )
                    // Bottom half of `add_stat.png` — `−1`. The caller is
                    // responsible for clamping at zero (different blocks
                    // wrap around differently — e.g. tennis points reset to
                    // 0 when a game is scored, not when decremented).
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .styleModifier {
                                property("bottom", "0")
                                property("left", "0")
                                property("right", "0")
                                property("height", "50%")
                            }
                            .cursor(Cursor.Pointer)
                            .onClick {
                                it.stopPropagation()
                                onDecrement()
                            }
                    )
                }

                // Always-open (mobile sheet) mode: the visible R2 pill fills
                // this whole pop-out box, but the overlay above is only 63%
                // of it — a finger on the pill's edge would land in the dead
                // margin and read as "the button doesn't work". Stretch the
                // hit halves over the full pop-out instead; there's no
                // tap-to-close here for them to shadow.
                if (steppersAlwaysOpen && isAdmin) {
                    listOf(true, false).forEach { isPlusHalf ->
                        Box(
                            modifier = Modifier
                                .position(Position.Absolute)
                                .styleModifier {
                                    property(if (isPlusHalf) "top" else "bottom", "0")
                                    property("left", "0")
                                    property("right", "0")
                                    property("height", "50%")
                                    // Kill double-tap zoom so rapid +/+/+ taps
                                    // all land as clicks.
                                    property("touch-action", "manipulation")
                                }
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    it.stopPropagation()
                                    if (isPlusHalf) onIncrement() else onDecrement()
                                }
                        )
                    }
                }
            }
        }

        // The tile frame itself — `stat.png` background filling the wrapper.
        // The `scaleX(-1)` mirror is applied HERE (not on the wrapper) so the
        // frame notch faces the card center on right-side tiles, while the
        // pop-out children outside this Box keep an un-mirrored coordinate
        // system and render on the geometrically-correct side of the card.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("background", "url('$blockUrl') center/contain no-repeat")
                    if (flipHorizontally) property("transform", "scaleX(-1)")
                }
                .then(
                    // Only admins can open the +/− stepper pill. Non-admin
                    // viewers see the counter values but the block isn't
                    // tappable — no cursor, no toggle.
                    if (isAdmin) {
                        Modifier.cursor(Cursor.Pointer).onClick {
                            it.stopPropagation()
                            open = !open
                        }
                    } else {
                        Modifier
                    }
                ),
            contentAlignment = Alignment.Center,
        ) {
            // Inner content — icon + counter. Un-flip so glyphs/digits read
            // right-side-up on right-tiles (the parent Box is mirrored above).
            Column(
                modifier = Modifier
                    .sGap(1)
                    .styleModifier {
                        if (flipHorizontally) property("transform", "scaleX(-1)")
                    },
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                when {
                    icon != null -> Image(
                        src = icon,
                        // Stat-block icons are SVGs with `fill=#BED6F5` baked
                        // in (Figma 7904:364601 text-secondary). No CSS filter
                        // needed — they render in the correct colour natively.
                        modifier = Modifier.sSize(iconSize),
                    )
                    iconTint != null -> Box(
                        modifier = Modifier
                            .sWidth(5).sHeight(6)
                            .sBorderRadius(1)
                            .styleModifier { property("background-color", iconTint) },
                    )
                }
                Box(
                    modifier = Modifier
                        .sWidth(7)
                        .sHeight(0.5)
                        .styleModifier {
                            property("background-color", "rgba(190, 214, 245, 0.5)")
                        }
                )
                SpanText(
                    valueFormatter(count),
                    modifier = Modifier
                        .sMarginTop(-1)
                        .sFontSize(7)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("line-height", s(8)) },
                )
            }
        }

    }
}

// ── Team Crest Side Panel ──

@Composable
private fun TeamCrestSide(
    team: EventTeamDto,
    showArrow: Boolean = false,
    onArrowClick: () -> Unit = {},
) {
    Column(
        modifier = Modifier
            .sPadding(14, 8)
            .styleModifier { property("border-radius", "${s(12)} 0 0 ${s(12)}") }
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border-top", "0.8px solid rgba(41,181,224,0.06)")
                property("border-left", "0.8px solid rgba(41,181,224,0.06)")
                property("border-bottom", "0.8px solid rgba(41,181,224,0.06)")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        // Team card
        Box(
            modifier = Modifier
                .sWidth(96)
                .sHeight(150)
                .sBorderRadius(10)
                .overflow(Overflow.Hidden)
                .position(Position.Relative)
                .styleModifier {
                    property("border", "0.583px solid #272E32")
                    property("background", team.color ?: "#03051F")
                },
            contentAlignment = Alignment.Center
        ) {
            // Background texture
            Image(
                src = "/images/team-card-texture.png",
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .fillMaxSize()
                    .sBorderRadius(10)
                    .styleModifier {
                        property("opacity", "0.55")
                        property("object-fit", "cover")
                        property("pointer-events", "none")
                    }
            )

            // Glow
            Image(
                src = "/images/team-card-glow.svg",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("width", s(178))
                        property("height", s(178))
                        property("left", s(-43))
                        property("top", s(-73))
                        property("pointer-events", "none")
                    }
            )

            // Deco
            Image(
                src = "/images/team-card-deco.svg",
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("width", s(272))
                        property("height", s(269))
                        property("left", "75%")
                        property("top", s(-100))
                        property("transform", "translateX(-8px) scaleY(-1) rotate(180deg)")
                        property("pointer-events", "none")
                    }
            )

            // Content
            Column(
                modifier = Modifier.zIndex(1).sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (team.officialTeamImageUrl != null) {
                    Image(
                        src = team.officialTeamImageUrl,
                        modifier = Modifier.sSize(40)
                            .styleModifier { property("object-fit", "contain") }
                    )
                } else {
                    Icon(src = AppIcons.TeamPeople, modifier = Modifier.sSize(40))
                }
                SpanText(
                    team.name.uppercase(),
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier {
                            property("text-align", "center")
                            property("letter-spacing", "0.1px")
                        }
                )
            }
        }

        // Down arrow — only shown when more than 2 teams. Clicking cycles
        // this slot to the next team in the event (skipping the team
        // shown in the opposite slot).
        if (showArrow) {
            Box(
                modifier = Modifier
                    .sMarginTop(12)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        it.stopPropagation()
                        onArrowClick()
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    src = AppIcons.ChevronDown,
                    modifier = Modifier.sSize(46).styleModifier {
                        property("filter", "brightness(0) invert(1)")
                        property("opacity", "0.6")
                    }
                )
            }
        }
    }
}

// ── Bench Row ──

@Composable
private fun BenchRow(
    event: EventDetailDto,
    visibleTeams: List<EventTeamDto>,
    isAdmin: Boolean,
    effectiveFormat: Int,
    slotPlayerOverrides: Map<String, String?>,
    selectedSlotKey: String?,
    participantById: Map<String, EventParticipantDto>,
    onTapSlot: (slotKey: String, currentPlayer: EventParticipantDto?) -> Unit,
    onViewProfile: (String) -> Unit,
    // Forwarded into `PlayerCardKit` so bench cards match the field
    // cards exactly — corner stat blocks, top-right star + decimal
    // rating, and the bottom-centre FIFA rating badge. Defaults keep the
    // live IN_PROGRESS bench unchanged (plain card, no rating chrome).
    playerStats: EventPlayerStatsResultDto? = null,
    onSetPlayerStat: (userId: String, statTypeId: String, value: Int) -> Unit =
        { _, _, _ -> },
    showRating: Boolean = false,
    hideZeroStats: Boolean = false,
    onPlayerClick: ((EventParticipantDto) -> Unit)? = null,
    eventRatingAverages: Map<String, Double> = emptyMap(),
) {
    val format = effectiveFormat

    val hasBench = visibleTeams.any { team ->
        event.participants.count { it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED" } > format
    }
    if (!hasBench) return

    val cardSize = if (format >= 11) PlayerCardSize.Highlight else PlayerCardSize.XtraSmall

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingBottom(12)
            .sPaddingLeft(16)
            .sPaddingRight(16)
            // `overflow: visible` so the 2× `transform: scale(...)` on bench
            // cards isn't clipped by the row's intrinsic (unscaled) height.
            // `Overflow.Auto` was clipping the upper half of every scaled
            // card — the flex row's bounds were locked to the layout-size
            // of an XtraSmall card (90 px tall), not its rendered 180 px.
            .styleModifier { property("overflow", "visible") }
            .styleModifier { property("scrollbar-width", "none") },
        // Left team's bench anchors to the LEFT edge of the screen and right
        // team's to the RIGHT edge — subs visually stay on their own team's
        // side of the field instead of clustering in the middle.
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Bottom
    ) {
        visibleTeams.forEachIndexed { teamIdx, team ->
            val confirmed = event.participants
                .filter { it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED" }
            val benchPlayers = confirmed.drop(format)

            Row(
                // Each bench card is `transform: scale(2)`-d, so visually
                // it extends ~58 design px past its layout box on the
                // side opposite the transform-origin. An 8-px layout gap
                // leaves ~50 px of visual overlap between neighbouring
                // cards; bumping the gap to 70 design px clears the
                // overlap and gives a small breathing space too.
                modifier = Modifier.sGap(70),
                verticalAlignment = Alignment.Bottom
            ) {
                benchPlayers.forEachIndexed { benchIdx, defaultPlayer ->
                    val slotKey = "bench-$teamIdx-$benchIdx"
                    val player = if (slotPlayerOverrides.containsKey(slotKey)) {
                        slotPlayerOverrides[slotKey]?.let { participantById[it] }
                    } else {
                        defaultPlayer
                    }
                    val isSelected = selectedSlotKey == slotKey
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                // Bench cards render at 2× their intrinsic
                                // (XtraSmall/Highlight) size so they're
                                // visible alongside the scaled-up field
                                // cards. Selection still bumps an extra
                                // ~10% on top.
                                val s = if (isSelected) "2.2" else "2.0"
                                property("transform", "scale($s)")
                                property("transform-origin", if (teamIdx == 0) "left bottom" else "right bottom")
                                property(
                                    "transition",
                                    "transform 0.85s cubic-bezier(0.22, 1, 0.36, 1)," +
                                        " filter 0.85s ease-out",
                                )
                                if (isSelected) {
                                    property(
                                        "filter",
                                        "drop-shadow(0 0 14px rgba(60,121,241,0.85))" +
                                            " drop-shadow(0 0 4px rgba(255,255,255,0.6))",
                                    )
                                    property("z-index", "1000")
                                }
                            }
                            .then(
                                if (isAdmin) {
                                    Modifier.cursor(Cursor.Pointer).onClick {
                                        it.stopPropagation()
                                        onTapSlot(slotKey, player)
                                    }
                                } else {
                                    Modifier
                                }
                            )
                    ) {
                        if (player != null) {
                            // Reuse the same kit the field cards use so
                            // bench substitutes carry the four corner
                            // stat tiles + the star/decimal rating + the
                            // bottom-centre FIFA rating badge. The
                            // wrapper above already scales the kit 2× via
                            // its own `transform: scale(2)`, so the kit's
                            // internal `cardScale` is held at `1.0` to
                            // avoid compounding.
                            PlayerCardKit(
                                player = player,
                                cardSize = cardSize,
                                cardScale = 1.0,
                                sport = event.sportName,
                                showStatBlocks = true,
                                isAdmin = isAdmin,
                                playerStats = playerStats,
                                onSetPlayerStat = onSetPlayerStat,
                                showRating = showRating,
                                hideZeroStats = hideZeroStats,
                                onPlayerClick = onPlayerClick,
                                eventRatingAverage = eventRatingAverages[player.userId],
                            )
                        } else {
                            // Bench slot whose original occupant was swapped
                            // onto the field — render the dimmed R2 blank
                            // card image so the slot stays tappable for a
                            // return-swap.
                            Box(
                                modifier = Modifier
                                    .sWidth(cardSize.w).sHeight(cardSize.h)
                                    .styleModifier {
                                        property("filter", "brightness(0.35)")
                                        property(
                                            "background",
                                            "url('${PLAYER_KIT_CDN_BASE}football_card.png')" +
                                                " center/contain no-repeat",
                                        )
                                    }
                            )
                        }
                    }
                }

            }
        }
    }
}

@Composable
private fun CoachCard() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sWidth(48)
            .sHeight(120)
            .styleModifier { property("border-radius", "${s(10)} ${s(4)} ${s(4)} ${s(4)}") }
            .overflow(Overflow.Hidden)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(229deg, rgba(56,124,255,0.27) 69.7%, rgba(5,4,12,0.03) 138.3%)"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            t.eventCoach,
            modifier = Modifier
                .sFontSize(11)
                .fontWeight(FontWeight.Medium)
                .color(AppColors.Text.Primary)
                .styleModifier {
                    property("writing-mode", "vertical-rl")
                    property("transform", "rotate(180deg)")
                    property("letter-spacing", s(0.026))
                }
        )
    }
}

// ── Formation Utilities ──

internal fun parseFormation(name: String): List<Int> {
    val trimmed = name.trim()
    // Tiny formats (1 a side) — no outfield rows, GK only.
    if (trimmed.isEmpty()) return listOf(1)
    val parts = trimmed.split("-").mapNotNull { it.trim().toIntOrNull() }
    return if (parts.isNotEmpty()) listOf(1) + parts else listOf(1, 4, 4, 2)
}

/**
 * Default formation per side size. [parseFormation] prepends `1` (the GK
 * slot) to whatever this returns, so e.g. `"2-3-1"` for 7-a-side ends up
 * as `[1,2,3,1]` = 7 slots total. Tiny formats (1-3) use an empty/short
 * string so the prepend gives `[1]`, `[1,1]`, `[1,2]` respectively; admins
 * can drag those wherever they want via the inline toolbar.
 */
internal fun defaultFormationForFormat(format: Int): String = when (format) {
    1 -> ""
    2 -> "1"
    3 -> "2"
    4 -> "2-1"
    5 -> "2-1-1"
    6 -> "2-2-1"
    7 -> "2-3-1"
    8 -> "3-3-1"
    9 -> "3-4-1"
    10 -> "4-3-2"
    11 -> "4-4-2"
    else -> "2-1-1"
}

private fun positionToRow(position: String?, totalRows: Int): Int {
    return when (position?.uppercase()) {
        "GK" -> 0
        "CB", "RB", "LB", "RWB", "LWB" -> 1
        "CDM", "CM", "CAM", "RM", "LM" -> if (totalRows >= 5) 2 else minOf(2, totalRows - 2)
        "RW", "LW", "ST", "CF", "SS" -> totalRows - 1
        else -> -1
    }
}

internal fun assignPlayersToFormation(
    players: List<EventParticipantDto>,
    rows: List<Int>
): List<List<EventParticipantDto?>> {
    val totalRows = rows.size
    val slots = rows.map { arrayOfNulls<EventParticipantDto>(it).toMutableList() }
    val placed = mutableSetOf<String>()

    // First pass: place players with known positions
    for (player in players) {
        val targetRow = positionToRow(player.position, totalRows)
        if (targetRow >= 0 && targetRow < totalRows) {
            val row = slots[targetRow]
            val emptyIdx = row.indexOfFirst { it == null }
            if (emptyIdx >= 0) {
                row[emptyIdx] = player
                placed.add(player.id)
            }
        }
    }

    // Second pass: fill remaining slots sequentially with unplaced players
    val unplaced = players.filter { it.id !in placed }
    var unplacedIdx = 0
    for (row in slots) {
        for (i in row.indices) {
            if (row[i] == null && unplacedIdx < unplaced.size) {
                row[i] = unplaced[unplacedIdx++]
            }
        }
    }

    return slots
}

/**
 * Distribute formation rows evenly inside the field. The X band starts at
 * 18% — the team crest pinned to the left/right edge is ~12% wide, and
 * with `scale(3)` the player card extends ~6% past its anchor on each
 * side, so 18% guarantees zero overlap with the crest. Band ends at 48%
 * (4% gap from the half-line). Layout starts FROM THE CENTRE and grows
 * outward: GK closest to the half-line, attackers near the team's own
 * edge. Right team mirrors via `100 - x`.
 */
/**
 * CSS `scale()` factor applied to each player card on the field. Formats
 * 1–5 keep the full 3× scale that matches the Figma reference; from 6
 * onwards the cards shrink (~0.25 per step) so the extra players have
 * room to sit side-by-side without colliding. 11×11 lands at 1.5× — half
 * the area of 1×1, two thirds the linear footprint.
 */
/**
 * The four corner StatBlocks on the live match player card map to backend
 * stat-type `displayKey`s — one per (sport, block-position) pair. Order:
 * `[topLeft, topRight, bottomLeft, bottomRight]`. A `null` slot means the
 * sport doesn't have an analogue for that block (no persistence, but the
 * UI keeps the same 4-block layout for visual consistency).
 */
internal fun statBlockKeysForSport(sport: String): List<String?> = when (sport.lowercase()) {
    "football" -> listOf("assists", "goals", "yellow_cards", "red_cards")
    "basketball" -> listOf("assists", "points", "rebounds", "steals")
    "tennis" -> listOf("points", "games", "aces", "double_faults")
    "padel" -> listOf("points", "games", "winners", "unforced_errors")
    "volleyball" -> listOf("assists", "kills", "blocks", "digs")
    else -> listOf(null, null, null, null)
}

private fun cardScaleForFormat(format: Int): Double = when {
    format <= 5 -> 3.0
    format >= 11 -> 1.5
    else -> 3.0 - (format - 5) * 0.25
}

/**
 * Hand-tuned default positions per format, recorded by an admin via the
 * inline `Log` button. Only the LEFT team (team idx 0) is stored — the
 * right team is rendered as a mirror via `100 - x` at draw time. If a
 * format isn't listed, the renderer falls back to the auto formula
 * ([rowXPercentsForTeam] + the Y-band formula inside [FieldFormationArea]).
 */
internal fun defaultPositionsForFormat(format: Int): Map<String, Pair<Double, Double>> = when (format) {
    1 -> mapOf(
        "0-0-0" to (35.49 to 14.88),
    )
    2 -> mapOf(
        "0-0-0" to (36.41 to -8.88),
        "0-1-0" to (31.62 to 34.39),
    )
    3 -> mapOf(
        "0-0-0" to (43.99 to 13.46),
        "0-1-0" to (33.66 to -9.34),
        "0-1-1" to (29.09 to 35.85),
    )
    4 -> mapOf(
        "0-0-0" to (44.9 to 12.85),
        "0-1-0" to (34.96 to -8.53),
        "0-1-1" to (30.82 to 38.09),
        "0-2-0" to (20.38 to 12.04),
    )
    5 -> mapOf(
        "0-0-0" to (43.57 to 36.02),
        "0-1-0" to (44.32 to -8.94),
        "0-1-1" to (31.25 to 36.66),
        "0-2-0" to (32.0 to -9.49),
        "0-3-0" to (19.61 to 13.87),
    )
    6 -> mapOf(
        "0-0-0" to (44.63 to 20.03),
        "0-1-0" to (36.41 to 0.19),
        "0-1-1" to (32.91 to 40.86),
        "0-2-0" to (27.38 to -10.0),
        "0-2-1" to (23.04 to 49.68),
        "0-3-0" to (18.0 to 10.59),
    )
    7 -> mapOf(
        "0-0-0" to (45.38 to 26.72),
        "0-1-0" to (40.88 to -4.72),
        "0-1-1" to (34.56 to 43.62),
        "0-2-0" to (32.19 to -10.0),
        "0-2-1" to (26.0 to 22.69),
        "0-2-2" to (19.47 to 55.0),
        "0-3-0" to (18.34 to 9.01),
    )
    8 -> mapOf(
        "0-0-0" to (45.79 to 3.5),
        "0-1-0" to (37.51 to -0.98),
        "0-1-1" to (41.92 to 33.31),
        "0-1-2" to (32.84 to 45.95),
        "0-2-0" to (29.16 to -10.0),
        "0-2-1" to (25.52 to 19.94),
        "0-2-2" to (22.22 to 50.7),
        "0-3-0" to (18.0 to 9.21),
    )
    9 -> mapOf(
        "0-0-0" to (45.38 to 24.36),
        "0-1-0" to (46.11 to -4.72),
        "0-1-1" to (33.32 to 42.36),
        "0-1-2" to (44.12 to 52.27),
        "0-2-0" to (37.49 to 6.52),
        "0-2-1" to (28.89 to -7.42),
        "0-2-2" to (25.52 to 20.16),
        "0-2-3" to (19.92 to 49.52),
        "0-3-0" to (18.0 to 9.41),
    )
    10 -> mapOf(
        "0-0-0" to (44.28 to 19.04),
        "0-1-0" to (44.67 to -5.11),
        "0-1-1" to (36.14 to -6.24),
        "0-1-2" to (42.67 to 44.95),
        "0-1-3" to (34.35 to 21.19),
        "0-2-0" to (27.65 to -7.08),
        "0-2-1" to (24.83 to 21.71),
        "0-2-2" to (32.67 to 48.54),
        "0-3-0" to (18.0 to 11.01),
        "0-3-1" to (21.16 to 51.06),
    )
    11 -> mapOf(
        "0-0-0" to (46.69 to 17.08),
        "0-1-0" to (42.74 to -1.57),
        "0-1-1" to (35.79 to -6.44),
        "0-1-2" to (36.34 to 20.16),
        "0-1-3" to (41.98 to 41.85),
        "0-2-0" to (28.48 to -10.0),
        "0-2-1" to (29.78 to 10.86),
        "0-2-2" to (32.74 to 47.7),
        "0-2-3" to (26.07 to 32.6),
        "0-3-0" to (20.06 to 10.22),
        "0-3-1" to (21.23 to 55.0),
    )
    else -> emptyMap()
}

private fun rowXPercentsForTeam(rowCount: Int, isLeftTeam: Boolean): List<Double> {
    val start = 18.0
    val end = 48.0
    val outwardFromCentre = if (rowCount <= 1) {
        listOf(end)
    } else {
        (0 until rowCount).map { end - (end - start) * it / (rowCount - 1) }
    }
    return if (isLeftTeam) outwardFromCentre else outwardFromCentre.map { 100.0 - it }
}
